import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy, inject } from '@angular/core';
import { SchedulerComponent } from '@progress/kendo-angular-scheduler';

@Directive({
  selector: 'kendo-scheduler',
  standalone: true,
})
export class KendoSchedulerLayoutDirective implements AfterViewInit, OnDestroy {
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly scheduler = inject(SchedulerComponent);
  private readonly zone = inject(NgZone);

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private frameId?: number;
  private secondFrameId?: number;

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      this.observeSchedulerHost();
      this.scheduleLayoutRefresh();
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.cancelLayoutRefresh();
  }

  private observeSchedulerHost(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleLayoutRefresh());
      this.resizeObserver.observe(this.elementRef.nativeElement);
    }

    if (typeof MutationObserver !== 'undefined') {
      this.mutationObserver = new MutationObserver((mutations) => {
        const shouldRefresh = mutations.some((mutation) => {
          return (
            mutation.type === 'childList' ||
            (mutation.type === 'attributes' &&
              (mutation.target as Element).parentElement === this.elementRef.nativeElement)
          );
        });

        if (shouldRefresh) {
          this.scheduleLayoutRefresh();
        }
      });
      this.mutationObserver.observe(this.elementRef.nativeElement, {
        attributeFilter: ['style'],
        attributes: true,
        childList: true,
        subtree: true,
      });
    }
  }

  private scheduleLayoutRefresh(): void {
    this.cancelLayoutRefresh();

    this.frameId = requestAnimationFrame(() => {
      this.frameId = undefined;
      this.secondFrameId = requestAnimationFrame(() => {
        this.secondFrameId = undefined;
        this.zone.run(() => this.scheduler.onResize());
      });
    });
  }

  private cancelLayoutRefresh(): void {
    if (this.frameId !== undefined) {
      cancelAnimationFrame(this.frameId);
      this.frameId = undefined;
    }

    if (this.secondFrameId !== undefined) {
      cancelAnimationFrame(this.secondFrameId);
      this.secondFrameId = undefined;
    }
  }
}
